import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchOssOrders } from "../../api/ossApi";
import { mockOrders } from "../../constants/mockData";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";
import { OssPanel } from "../common/OssPanel";
import { OssDashboardConnectedCalendar } from "./OssDashboardConnectedCalendar";
import { OssDashboardGanttView } from "./OssDashboardGanttView";
import { OssDashboardWorkFocus } from "./OssDashboardWorkFocus";
import { normalizeTimelineRows } from "../../utils/timelineUtils";

const viewOptions = [
  { key: "today", label: "오늘 할 일", icon: <TodayOutlinedIcon fontSize="small" /> },
  { key: "calendar", label: "일정 보기", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { key: "gantt", label: "장기 흐름", icon: <ViewTimelineOutlinedIcon fontSize="small" /> },
];

export const OssDashboardTimelinePanel = ({ onOpenOrder }) => {
  const { group_code, project_code } = useParams();
  const [view, setView] = useState("today");
  const { data, loading, error, load } = useOssAsyncData({
    fetcher: () =>
      fetchOssOrders(group_code, project_code, {
        page: 1,
        pageSize: 100,
        sortBy: "requiredStartDate",
        sortDir: "ASC",
      }),
    fallbackData: { items: mockOrders, pagination: { total: mockOrders.length } },
    errorMessage: "일정 데이터를 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });

  const rows = useMemo(
    () => normalizeTimelineRows(data?.items?.length ? data.items : mockOrders),
    [data]
  );

  return (
    <OssPanel
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.2}
        sx={{ mb: 1.05, flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>
            {view === "today" ? "오늘 업무 한눈에 보기" : view === "calendar" ? "일정 보기" : "장기 흐름 보기"}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
            {view === "today"
              ? "오늘 확인할 주문과 이번주·이번달 일정을 먼저 확인합니다."
              : view === "calendar"
                ? "연/월/주/일 단위로 착수일부터 요청 납기까지의 기간을 봅니다."
                : "장기납기 주문의 착수-납기 흐름을 넓게 확인합니다."}
          </Typography>
        </Box>
        <Tabs
          value={view}
          onChange={(_event, next) => setView(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 38,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 38,
              minWidth: 102,
              px: 1.4,
              py: 0.7,
              mr: 0.55,
              border: "1px solid #dbe2ef",
              borderRadius: "8px",
              color: "#475569",
              fontSize: "13px !important",
              fontWeight: "800 !important",
              bgcolor: "#fff",
            },
            "& .Mui-selected": {
              color: "#fff !important",
              borderColor: "#6c5ce7",
              bgcolor: "#6c5ce7",
              boxShadow: "0 8px 18px rgba(108, 92, 231, 0.22)",
            },
          }}
        >
          {viewOptions.map((option) => (
            <Tab key={option.key} value={option.key} icon={option.icon} iconPosition="start" label={option.label} />
          ))}
        </Tabs>
      </Stack>

      {error && (
        <Alert severity="error" action={<Button color="inherit" onClick={load}>재시도</Button>} sx={{ mb: 1, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, height: "100%", maxHeight: "100%", display: "flex", overflow: "hidden" }}>
        {view === "today" && <OssDashboardWorkFocus rows={rows} onOpenOrder={onOpenOrder} />}
        {view === "calendar" && (
          <OssDashboardConnectedCalendar rows={rows} onOpenOrder={onOpenOrder} />
        )}
        {view === "gantt" && (
          <OssDashboardGanttView rows={rows} loading={loading} onOpenOrder={onOpenOrder} />
        )}
      </Box>
    </OssPanel>
  );
};
